import { memo, useCallback } from "react";

import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import TableSimple from "ds/components/TableSimple";
import useTofuWorkspacesStore from "shared/Stack/useSearchTofuWorkspaces/store";
import { TofuWorkspace } from "types/generated";

import STACK_WORKSPACES_CELLS from "./Cells";
import {
  STACK_WORKSPACES_LIMIT_STORAGE_KEY,
  STACK_WORKSPACES_PAGINATION_OPTIONS,
  STACK_WORKSPACES_SORT_CONFIG,
  STACK_WORKSPACES_SORT_STORAGE_KEY,
} from "./constants";
import useStackWorkspacesTableStore from "./useStore";

type StackWorkspacesTableProps = {
  workspaces: TofuWorkspace[];
  loading: boolean;
  startCursor: string | undefined;
  endCursor: string | undefined;
  totalCount: number;
};

const StackWorkspacesTable = ({
  workspaces,
  loading,
  startCursor,
  endCursor,
  totalCount,
}: StackWorkspacesTableProps) => {
  const { onFetchItems, rowsPerPage: initialLimit, resetCursor } = useTofuWorkspacesStore();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: onFetchItems,
    startCursor,
    endCursor,
    allItemsCount: totalCount,
    visibleItemsCount: workspaces.length,
    initialLimit,
    storageKey: STACK_WORKSPACES_LIMIT_STORAGE_KEY,
  });

  const handleSortChange = useCallback(() => {
    resetCursor();
  }, [resetCursor]);

  return (
    <>
      <TableSimple
        data={workspaces}
        loading={loading}
        store={useStackWorkspacesTableStore}
        ariaLabel="Stack workspaces table"
        cellPerColumn={STACK_WORKSPACES_CELLS}
        filtersStorageKey={STACK_WORKSPACES_SORT_STORAGE_KEY}
        sortConfig={STACK_WORKSPACES_SORT_CONFIG}
        sortCallback={handleSortChange}
      />

      <TablePagination
        from={from}
        to={to}
        loading={loading}
        goToNextPage={goToNextPage}
        setRowsPerPage={setRowsPerPage}
        goToPrevPage={goToPrevPage}
        goToFirstPage={goToFirstPage}
        goToLastPage={goToLastPage}
        rowsPerPage={rowsPerPage}
        itemsCount={totalCount}
        lastPage={lastPage}
        page={page}
        paginationOptions={STACK_WORKSPACES_PAGINATION_OPTIONS}
      />
    </>
  );
};

export default memo(StackWorkspacesTable);
